import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { SEVERITY_TYPES, STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { IAlertState } from '../../hooks/useAlertState/useAlertState';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Button, IButtonProps } from '../Button/Button';
import { BUTTON_MODES, BUTTON_VARIANTS } from '../Button/buttonVariants';
import { CloseButton } from '../CloseButton/CloseButton';
import { StatusIcon } from '../StatusIcon/StatusIcon';
import { EmailInput, IEmailInputProps } from '../TextField/EmailInput';
import { IPasswordInputProps, PasswordInput } from '../TextField/PasswordInput';
import { ITextFieldProps, TextField } from '../TextField/TextField';
import { Span } from '../Texts/Texts';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';
import { MEDIA_QUERY } from '../../fundamentals/breakpoints/breakpoints';
import { ResponsiveGapContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';

export const noticebarColours = ({ base, palette }: IColoursCollection) => ({
  noticebarColours: {
    text: base.white.colour,
    [STATUS_COLOURS.INFO]: {
      background: palette.info600
    },
    [STATUS_COLOURS.WARNING]: {
      background: palette.warning600
    },
    [STATUS_COLOURS.ERROR]: {
      background: palette.error600
    }
  }
});

export type NoticebarVariant = typeof STATUS_COLOURS.ERROR | typeof STATUS_COLOURS.WARNING | typeof STATUS_COLOURS.INFO;
export type NoticebarPosition = 'top' | 'bottom';

export interface INoticebarTextFieldProps extends Omit<ITextFieldProps, 'inputSize'> {}
export interface INoticebarEmailInputProps extends Omit<IEmailInputProps, 'inputSize'> {}
export interface INoticebarPasswordInputProps extends Omit<IPasswordInputProps, 'inputSize'> {}
export interface INoticebarButtonProps extends Omit<IButtonProps, 'size' | 'variant' | 'fullWidth' | 'constantWidth'> {}

export interface INoticebarProps extends Omit<IAlertState, 'isProcessing' | 'title'> {
  variant?: NoticebarVariant;
  position?: NoticebarPosition;
  confirmButton?: INoticebarButtonProps;
  cancelButton?: INoticebarButtonProps;
  emailInput?: INoticebarEmailInputProps;
  passwordInput?: INoticebarPasswordInputProps;
  textInput?: INoticebarTextFieldProps;
  fixed?: boolean;
}

const Container = styled(ResponsiveGapContainer)<Pick<INoticebarProps, 'variant'>>`
  background: ${({ theme, variant }) => theme.alertColours[variant!].default.background};
  border-left: ${({ theme, variant }) => theme.alertColours[variant!].default.border};
  align-items: flex-start;
  width: 100%;
  justify-content: space-between;
  border: none;
  background: ${({ theme, variant }) => theme.noticebarColours[variant!].background};
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.MEDIUM]} {
    flex-direction: row;
  }
`;

const Controls = styled(BaseDiv)<{ hasControls: boolean }>`
  display: flex;
  align-self: auto;
  gap: inherit;
  justify-content: flex-end;
  flex-direction: column;
  width: 100%;

  ${({ theme }) => theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.MEDIUM]} {
    flex-direction: row;
    width: auto;
  }

  ${({ theme, hasControls }) =>
    !hasControls &&
    css`
      ${theme.mediaQueries[MEDIA_QUERY.MAX][SIZES.MEDIUM]} {
        display: none;
      }
    `};
`;

const Message = styled(BaseDiv)`
  display: flex;
  gap: inherit;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
`;

const NoticebarCloseButton = styled(CloseButton)<{ showOnMobile?: boolean }>`
  display: none;
  ${({ showOnMobile, theme }) =>
    showOnMobile &&
    css`
      ${theme.mediaQueries[MEDIA_QUERY.MAX][SIZES.MEDIUM]} {
        display: inherit;
      }
    `};
  ${({ showOnMobile, theme }) =>
    !showOnMobile &&
    css`
      ${theme.mediaQueries[MEDIA_QUERY.MIN][SIZES.MEDIUM]} {
        display: inherit;
      }
    `};
`;

const NoticebarSpan = styled(Span)`
  width: 100%;
  ${({ theme }) => css`
    color: ${theme.noticebarColours.text};
    & * {
      color: ${theme.noticebarColours.text};
    }
  `};
`;
const NoticebarButton = styled(Button)`
  white-space: nowrap;
`;

export const Noticebar: React.FC<INoticebarProps> = ({ ...rest }) => {
  const { variant = STATUS_COLOURS.INFO, onClose, closable = false, emailInput, passwordInput, textInput, cancelButton, confirmButton, children } = rest;
  const { generateTestId } = useTestId(rest, 'Noticebar');
  const [closed, setClosed] = useState(false);

  if (closed) {
    return <></>;
  }

  const handleOnClose = (e: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setClosed(true);
    onClose?.(e);
  };

  return (
    <Container variant={variant} {...generateTestId()} gap='medium' gutter={'large'}>
      <Message>
        <StatusIcon variant={variant} severityType={SEVERITY_TYPES.WHITE} size={SIZES.X_SMALL} {...generateTestId('Icon')} />
        <NoticebarSpan fontWeightCss={FONT_WEIGHTS.REGULAR} {...generateTestId('Text')}>
          {children}
        </NoticebarSpan>
        {closable && (
          <NoticebarCloseButton
            {...generateTestId('Close.Mobile')}
            showOnMobile={true}
            size={SIZES.X_SMALL}
            onClick={handleOnClose}
            minimal
            variant='white50'
          />
        )}
      </Message>
      <Controls hasControls={!!(textInput || emailInput || passwordInput || cancelButton || confirmButton)}>
        {(textInput && <TextField {...textInput} {...generateTestId('TextInput')} />) ||
          (emailInput && <EmailInput {...emailInput} {...generateTestId('EmailInput')} />) ||
          (passwordInput && <PasswordInput {...passwordInput} {...generateTestId('PasswordInput')} />)}
        {cancelButton && (
          <NoticebarButton fullWidth={true} variant={BUTTON_VARIANTS.WHITE} mode={BUTTON_MODES.OUTLINE} {...cancelButton} {...generateTestId('Cancel')} />
        )}
        {confirmButton && <NoticebarButton fullWidth={true} variant={BUTTON_VARIANTS.WHITE} {...confirmButton} {...generateTestId('Confirm')} />}
        {closable && (
          <NoticebarCloseButton
            {...generateTestId('Close.Large')}
            showOnMobile={false}
            size={SIZES.X_SMALL}
            onClick={handleOnClose}
            minimal
            variant='white50'
          />
        )}
      </Controls>
    </Container>
  );
};
